import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getIchimokuEntryConditions } from '../../api/tradeGroups';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import './IchimokuExitConditionsBar.css';

/**
 * Props for IchimokuExitConditionsBar.
 * Shares the same fetch as IchimokuEntryConditionsBar — identical queryKey →
 * TanStack dedupes → one HTTP call serves both surfaces (PLAN-045).
 */
export interface IchimokuExitConditionsBarProps {
  tradeGroupId: string;
  className?: string;
}

const STALE_TIME_MS = 30_000;

const formatPrice = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
};

interface ExitSegmentSpec {
  key: string;
  shortLabel: string;
  fullLabel: string;
  value: boolean | null;
}

const statusFor = (value: boolean | null): { cls: string; symbol: string } => {
  // Exit booleans flip semantics: true = "exit fired" (red/critical),
  // false = "no exit fired" (green/healthy).
  if (value === null) return { cls: 'warmup', symbol: '–' };
  if (value === true) return { cls: 'fired', symbol: '!' };
  return { cls: 'clear', symbol: '✓' };
};

const IchimokuExitConditionsBar: React.FC<IchimokuExitConditionsBarProps> = ({
  tradeGroupId,
  className = '',
}) => {
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');

  // SAME queryKey as IchimokuEntryConditionsBar — TanStack dedupes.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ichimoku-entry-conditions', tradeGroupId, refreshTrigger],
    queryFn: () => getIchimokuEntryConditions(tradeGroupId),
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`ichimoku-exit-conditions-bar loading ${className}`}>
        <div className="ichimoku-exit-gauge" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    current_price,
    current_stop_price,
    stop_style,
    exit_conditions,
  } = data;

  const segments: ExitSegmentSpec[] = [
    { key: 'tk_reversal',              shortLabel: 'TK↓',  fullLabel: 'Tenkan/Kijun Reversal',  value: exit_conditions?.tk_reversal ?? null },
    { key: 'close_below_cloud',        shortLabel: 'P<C',  fullLabel: 'Close Below Cloud',      value: exit_conditions?.close_below_cloud ?? null },
    { key: 'chikou_below_price_lag26', shortLabel: 'Chk↓', fullLabel: 'Chikou < Price (−26)',   value: exit_conditions?.chikou_below_price_lag26 ?? null },
  ];

  // Stop-loss proximity: % distance from current price to stop trigger.
  // Negative = stop is below current price (the normal long-side configuration).
  const stopProximityPct =
    current_price !== null && current_stop_price !== null && current_price > 0
      ? ((current_stop_price - current_price) / current_price) * 100
      : null;

  const anyExitFired = exit_conditions?.any_exit_fired === true;

  return (
    <div className={`ichimoku-exit-conditions-bar ${className}`}>
      <div className="ichimoku-exit-header-row">
        <span className="ichimoku-exit-label">Exit Conditions:</span>
        <span
          className={`ichimoku-exit-summary ${anyExitFired ? 'fired' : 'clear'}`}
          role="status"
          aria-label={anyExitFired ? 'At least one exit condition fired' : 'No exit conditions fired'}
        >
          {anyExitFired ? 'Exit Triggered' : 'Monitoring'}
        </span>
      </div>

      <div
        className="ichimoku-exit-gauge"
        role="img"
        aria-label="Ichimoku exit-conditions gauge — three segments"
      >
        {segments.map(seg => {
          const { cls, symbol } = statusFor(seg.value);
          return (
            <div
              key={seg.key}
              className={`ichimoku-exit-segment ${cls}`}
              title={`${seg.fullLabel}: ${seg.value === null ? 'warmup' : seg.value ? 'fired' : 'clear'}`}
              aria-label={`${seg.fullLabel}: ${seg.value === null ? 'warmup' : seg.value ? 'fired' : 'clear'}`}
            >
              <span className="ichimoku-exit-segment-symbol">{symbol}</span>
              <span className="ichimoku-exit-segment-label">{seg.shortLabel}</span>
            </div>
          );
        })}
      </div>

      <div className="ichimoku-exit-stop-row">
        <div className="ichimoku-exit-stop-cell">
          <span className="ichimoku-exit-stop-label">Stop Style</span>
          <span className="ichimoku-exit-stop-value">{stop_style || '—'}</span>
        </div>
        <div className="ichimoku-exit-stop-cell">
          <span className="ichimoku-exit-stop-label">Stop Price</span>
          <span className="ichimoku-exit-stop-value">{formatPrice(current_stop_price)}</span>
        </div>
        <div className="ichimoku-exit-stop-cell">
          <span className="ichimoku-exit-stop-label">Stop Distance</span>
          <span className="ichimoku-exit-stop-value">
            {stopProximityPct === null
              ? '—'
              : `${stopProximityPct > 0 ? '+' : ''}${stopProximityPct.toFixed(2)}%`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default IchimokuExitConditionsBar;
